import {
  Grid,
  Paper,
  Stack,
  Typography,
  TextField,
  Avatar,
  Box,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Bolt,
  Smartphone,
  Close as CloseIcon,
  Save as SaveIcon,
  ContentCopy,
} from "@mui/icons-material";
import PhoneFrame from "./PhoneFrame";
import SectionHeader from "./SectionHeader";
import { useCallback, useRef } from "react";

const SmsEditor = ({ form, updateField, tags, errors }) => {
  const smsRef = useRef(null);

  // insert-at-caret helpers
  function insertAtCursor(textareaEl, currentValue, snippet) {
    const start = textareaEl?.selectionStart ?? currentValue.length;
    const end = textareaEl?.selectionEnd ?? currentValue.length;
    const next =
      currentValue.slice(0, start) + snippet + currentValue.slice(end);
    return { next, caret: start + snippet.length };
  }

  const insertSmsVar = useCallback(
    (snippet) => {
      const el = smsRef.current?.querySelector("textarea");
      const value = form.sms_text_template || "";
      const { next, caret } = insertAtCursor(el, value, snippet);
      updateField({ target: { name: "sms_text_template", value: next } });
      requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(caret, caret);
      });
    },
    [form.sms_text_template, updateField],
  );

  const removeTag = useCallback(
    (tagToRemove) => {
      const currentTags = tags.filter((t) => t !== tagToRemove);
      const newRequiredVars = currentTags.join(",");
      updateField({
        target: { name: "required_variables", value: newRequiredVars },
      });
    },
    [tags, updateField],
  );
  return (
    <>
      <Grid container spacing={2}>
        {/* LEFT: phone preview */}
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">
            LIVE PREVIEW
          </Typography>
          <Box mt={1.5}>
            <PhoneFrame>
              <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Stack spacing={1} alignItems="center">
                    <Avatar sx={{ bgcolor: "#e8eef9", color: "primary.main" }}>
                      <Smartphone fontSize="small" />
                    </Avatar>
                    <Typography variant="caption" color="text.secondary">
                      {form.sms_header || "YOUR BUSINESS"}
                    </Typography>
                  </Stack>

                  <Stack spacing={1}>
                    <Typography
                      variant="caption"
                      align="center"
                      color="text.secondary"
                    >
                      Today 10:45 AM
                    </Typography>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          ml: "auto",
                          maxWidth: "80%",
                          bgcolor: "primary.main",
                          color: "#fff",
                          p: 1.2,
                          borderRadius: "12px 12px 4px 12px",
                          fontSize: 13,
                          lineHeight: 1.4,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {form.sms_text_template ||
                          "Your SMS preview will appear here."}
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Delivered
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </PhoneFrame>
          </Box>
        </Grid>

        {/* RIGHT: panels */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <SectionHeader
                  icon={<Bolt fontSize="small" />}
                  title="General Details"
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Template Key"
                      fullWidth
                      autoComplete="off"
                      value={form.template_key}
                      name="template_key"
                      onChange={(e) => {
                        updateField(e);
                      }}
                      required
                      error={!!errors.template_key}
                      helperText={errors.template_key}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Template Name"
                      fullWidth
                      autoComplete="off"
                      value={form.template_name}
                      name="template_name"
                      onChange={(e) => {
                        updateField(e);
                      }}
                      error={!!errors.template_name}
                      helperText={errors.template_name}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="SMS Template ID"
                      fullWidth
                      autoComplete="off"
                      value={form.sms_template_id}
                      name="sms_template_id"
                      onChange={(e) => {
                        updateField(e);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="SMS Header"
                      fullWidth
                      autoComplete="off"
                      value={form.sms_header}
                      name="sms_header"
                      onChange={(e) => {
                        updateField(e);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Required Variables"
                      fullWidth
                      autoComplete="off"
                      value={form.required_variables}
                      name="required_variables"
                      onChange={(e) => {
                        updateField(e);
                      }}
                      helperText="Example: firstname,otp,order_id"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Tenant ID"
                      fullWidth
                      autoComplete="off"
                      value={form.tenant_id}
                      name="tenant_id"
                      required
                      onChange={(e) => {
                        updateField(e);
                      }}
                      error={!!errors.tenant_id}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <SectionHeader
                  icon={<Smartphone fontSize="small" />}
                  title="Message Body"
                  right={
                    <Typography variant="caption" color="text.secondary">
                      {form.sms_text_template.length || 0}
                    </Typography>
                  }
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  INSERT VARIABLES
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 1.5 }}
                >
                  {tags.map((t) => (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      onClick={() => insertSmsVar(t)}
                      onDelete={() => removeTag(t)}
                    />
                  ))}
                </Stack>

                <TextField
                  label="SMS Text Template"
                  fullWidth
                  multiline
                  minRows={5}
                  autoComplete="off"
                  value={form.sms_text_template}
                  name="sms_text_template"
                  inputRef={smsRef}
                  onChange={(e) => {
                    updateField(e);
                  }}
                  error={!!errors.sms_text_template}
                  helperText={
                    errors.sms_text_template ||
                    "Keep it under 160 chars to avoid message splitting."
                  }
                />

                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ mt: 1 }}
                >
                  <Tooltip title="Copy SMS">
                    <IconButton
                      onClick={() =>
                        navigator.clipboard.writeText(
                          form.sms_text_template || "",
                        )
                      }
                      size="small"
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default SmsEditor;
